import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import tmisWordmarkWhite from '../img/tmis_logo_white.png';
import tmisWordmarkColored from '../img/tmis_logo_colored.png';

const NAV_SECTIONS = [
  { label: 'MAIN', icon: 'fa-gauge-high', items: [
    { to: '/', label: 'Dashboard', icon: 'fa-gauge-high' },
  ]},
  { label: 'PROGRAMMES', icon: 'fa-graduation-cap', items: [
    { to: '/catalogue', label: 'Course Catalogue', icon: 'fa-book-open' },
    { to: '/course-levels', label: 'Course Level Master', icon: 'fa-bars-staggered' },
    { to: '/modules', label: 'Module Master', icon: 'fa-layer-group' },
    { to: '/intakes', label: 'Intake Master', icon: 'fa-calendar-check' },
    { to: '/components', label: 'Component Master', icon: 'fa-chart-pie' },
    { to: '/timetable', label: 'Timetable', icon: 'fa-calendar-days', badge: { text: '2', cls: 'nb-gold' } },
    { to: '/workflow', label: 'Approval Workflow', icon: 'fa-diagram-next', badge: { text: '2', cls: 'nb-sea' } },
  ]},
  { label: 'RESOURCES', icon: 'fa-boxes-stacked', items: [
    { to: '/lecturers', label: 'Lecturers', icon: 'fa-chalkboard-user' },
    { to: '/classrooms', label: 'Classrooms', icon: 'fa-door-open' },
  ]},
  { label: 'SPECIALISATIONS', icon: 'fa-star', items: [
    { to: '/ai', label: 'AI in Tourism', icon: 'fa-robot', badge: { text: 'New', cls: 'nb-coral' } },
    { to: '/social', label: 'Social Media', icon: 'fa-share-nodes', badge: { text: 'New', cls: 'nb-coral' } },
  ]},
  { label: 'INSIGHTS', icon: 'fa-chart-line', items: [
    { to: '/reports', label: 'Reports & Analytics', icon: 'fa-chart-bar' },
    { to: '/audit', label: 'Audit Trail', icon: 'fa-clock-rotate-left' },
  ]},
];

const TITLES = {
  '/': 'Dashboard',
  '/catalogue': 'Course Catalogue',
  '/course-levels': 'Course Level Master',
  '/modules': 'Module Master',
  '/intakes': 'Intake Master',
  '/components': 'Component Master',
  '/timetable': 'Timetable',
  '/workflow': 'Approval Workflow',
  '/lecturers': 'Lecturers',
  '/classrooms': 'Classrooms',
  '/ai': 'AI in Tourism',
  '/social': 'Social Media',
  '/reports': 'Reports & Analytics',
  '/audit': 'Audit Trail',
  '/settings': 'Settings',
  '/profile': 'My Profile',
};

const NOTIFICATIONS = [
  { icon: 'fa-triangle-exclamation', bg: 'var(--red-dim)', col: 'var(--red)', title: 'AI flagged conflict — TG201 & AI-TG101', sub: 'Guide trainer double-booked Tue 10:00 · Auto-fix available', time: '4 min ago', unread: true },
  { icon: 'fa-circle-check', bg: 'var(--green-dim)', col: 'var(--green)', title: 'AI Agent resolved 3 scheduling conflicts', sub: 'PLN-002 Diploma plan is now conflict-free', time: '1 hr ago', unread: true },
  { icon: 'fa-robot', bg: 'var(--violet-dim)', col: 'var(--violet)', title: 'AI generated course description — AI-TM301', sub: 'GenAI for Destination Marketing draft ready for review', time: '3 hrs ago', unread: false },
  { icon: 'fa-graduation-cap', bg: 'var(--gold-dim)', col: 'var(--gold)', title: 'SkillsFuture confirmed — SM-TM101', sub: 'Social Media for Tourism is now SSF eligible', time: 'Yesterday', unread: false },
];

function initials(name = '') {
  return name.split(' ').slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}

export default function AppShell() {
  const { user, displayRole, logout } = useAuth();
  const { theme, toggle, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('tmis_sidebar_collapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [npOpen, setNpOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [flyPos, setFlyPos] = useState({ top: 0, left: 0 });
  const [tip, setTip] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profilePos, setProfilePos] = useState({ bottom: 0, left: 0 });
  const npRef = useRef(null);
  const flyRef = useRef(null);
  const profileRef = useRef(null);
  const closeTimer = useRef(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (npOpen && npRef.current && !npRef.current.contains(e.target) && !e.target.closest('.tb-btn')) {
        setNpOpen(false);
      }
      if (openSection && flyRef.current && !flyRef.current.contains(e.target) && !e.target.closest('.ni-sec')) {
        setOpenSection(null);
      }
      if (profileOpen && profileRef.current && !profileRef.current.contains(e.target) && !e.target.closest('.sb-user')) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [npOpen, openSection, profileOpen]);

  useEffect(() => { setMobileOpen(false); setOpenSection(null); setTip(null); setProfileOpen(false); }, [location.pathname]);

  const openFlyout = (label, rowEl) => {
    clearTimeout(closeTimer.current);
    const rowRect = rowEl.getBoundingClientRect();
    const sidebarRect = document.getElementById('sidebar').getBoundingClientRect();
    const flyWidth = 230;
    setFlyPos({
      top: rowRect.top,
      left: Math.min(sidebarRect.right - 4, window.innerWidth - flyWidth - 8),
    });
    setOpenSection(label);
  };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setOpenSection(null), 160); };
  const cancelClose = () => clearTimeout(closeTimer.current);

  const showTip = (label, rowEl) => {
    if (!collapsed || isMobileView) return;
    const rowRect = rowEl.getBoundingClientRect();
    const sidebarRect = document.getElementById('sidebar').getBoundingClientRect();
    setTip({ label, top: rowRect.top + rowRect.height / 2, left: sidebarRect.right + 8 });
  };
  const hideTip = () => setTip(null);

  const toggleProfileMenu = (rowEl) => {
    const rowRect = rowEl.getBoundingClientRect();
    const sidebarRect = document.getElementById('sidebar').getBoundingClientRect();
    const menuWidth = 230;
    // On the mobile/tablet drawer the sidebar already spans (most of) the
    // viewport, so popping the menu out to its right would push it off
    // the visible drawer entirely — anchor it inside the drawer instead.
    const left = isMobileView
      ? Math.min(sidebarRect.left + 12, window.innerWidth - menuWidth - 8)
      : Math.min(sidebarRect.right + 8, window.innerWidth - menuWidth - 8);
    // On mobile/tablet the row sits at the very bottom of a full-height
    // drawer, so anchoring to its bottom edge makes the popup overlap the
    // row itself — anchor to the row's top edge instead so it opens fully
    // above the name, not on top of it.
    const bottom = isMobileView
      ? window.innerHeight - rowRect.top + 8
      : window.innerHeight - rowRect.bottom;
    setProfilePos({ bottom, left: Math.max(8, left) });
    setProfileOpen((o) => !o);
  };

  const toggleCollapsed = () => {
    setTip(null);
    setProfileOpen(false);
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('tmis_sidebar_collapsed', next ? '1' : '0');
      return next;
    });
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const ini = initials(user?.name);
  const title = TITLES[location.pathname] || 'TMIS CDP';
  const isDark = theme === 'dark';

  return (
    <div id="app" style={{ display: 'flex' }}>
      {mobileOpen && <div className="sb-backdrop" onClick={() => setMobileOpen(false)} />}
      <aside id="sidebar" className={`${collapsed ? 'col' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sb-head" onClick={() => { if (collapsed && !isMobileView) toggleCollapsed(); }}>
          <div
            className="sb-mark"
            onClick={(e) => { if (collapsed && !isMobileView) return; e.stopPropagation(); navigate('/'); }}
            role="button"
            tabIndex={0}
            aria-label={collapsed && !isMobileView ? 'Expand sidebar' : 'TMIS home'}
          >
            <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
              <rect width="64" height="64" rx="14" fill="#16337A" />
              <circle cx="32" cy="32" r="16" fill="#F26522" />
            </svg>
          </div>
          <div className="sb-br">
            <img src={isDark ? tmisWordmarkWhite : tmisWordmarkColored} alt="TMIS" className="sb-wordmark" />
            <span>Course Deployment</span>
          </div>
          <button className="sb-tog" onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-pressed={collapsed}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 3v18" />
            </svg>
          </button>
        </div>
        <nav className="sb-nav" aria-label="Main navigation">
          {NAV_SECTIONS.map((sec) => {
            if (sec.items.length === 1) {
              const item = sec.items[0];
              return (
                <NavLink
                  key={sec.label}
                  to={item.to}
                  end={item.to === '/'}
                  data-label={item.label}
                  className={({ isActive }) => `ni ${isActive ? 'active' : ''}`}
                  onMouseEnter={(e) => showTip(item.label, e.currentTarget)}
                  onMouseLeave={hideTip}
                >
                  <i className={`fa-solid ${item.icon}`} />
                  <span>{item.label}</span>
                </NavLink>
              );
            }
            const sectionActive = sec.items.some((i) => location.pathname === i.to);
            const isOpen = openSection === sec.label;
            return (
              <div key={sec.label}>
                <div
                  className={`ni ni-sec ${sectionActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                  data-label={sec.label}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={(e) => { if (!isMobileView) openFlyout(sec.label, e.currentTarget); }}
                  onMouseLeave={() => { if (!isMobileView) scheduleClose(); }}
                  onClick={(e) => (isMobileView ? setOpenSection((s) => (s === sec.label ? null : sec.label)) : openFlyout(sec.label, e.currentTarget))}
                >
                  <i className={`fa-solid ${sec.icon}`} />
                  <span>{sec.label.charAt(0) + sec.label.slice(1).toLowerCase()}</span>
                  <i className="fa-solid fa-chevron-right ni-chev" style={isMobileView && isOpen ? { transform: 'rotate(90deg)' } : undefined} />
                </div>
                {isMobileView && isOpen && (
                  <div className="ni-inline">
                    {sec.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive }) => `ni-sub ${isActive ? 'active' : ''}`}
                        onClick={() => setOpenSection(null)}
                      >
                        <i className={`fa-solid ${item.icon}`} />
                        <span>{item.label}</span>
                        {item.badge && <span className={`nb ${item.badge.cls}`}>{item.badge.text}</span>}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {!isMobileView && openSection && (() => {
          const sec = NAV_SECTIONS.find((s) => s.label === openSection);
          if (!sec) return null;
          return (
            <div
              ref={flyRef}
              className="sb-fly"
              style={{ top: flyPos.top, left: flyPos.left }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="sb-fly-title">{sec.label}</div>
              {sec.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `sb-fly-item ${isActive ? 'active' : ''}`}
                  onClick={() => setOpenSection(null)}
                >
                  <i className={`fa-solid ${item.icon}`} />
                  <span>{item.label}</span>
                  {item.badge && <span className={`nb ${item.badge.cls}`}>{item.badge.text}</span>}
                </NavLink>
              ))}
            </div>
          );
        })()}

        {collapsed && tip && (
          <div className="sb-tip" style={{ top: tip.top, left: tip.left }}>{tip.label}</div>
        )}
        <div className="sb-bot">
          {collapsed && !isMobileView ? (
            <button className="sb-theme-btn" onClick={toggle} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              <i className={`fa-solid ${isDark ? 'fa-sun' : 'fa-moon'}`} />
            </button>
          ) : (
            <div className="sb-theme-toggle" role="radiogroup" aria-label="Theme">
              <button
                type="button"
                role="radio"
                aria-checked={!isDark}
                className={`sb-theme-seg ${!isDark ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <i className="fa-solid fa-sun" />
                <span>Light</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={isDark}
                className={`sb-theme-seg ${isDark ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <i className="fa-solid fa-moon" />
                <span>Dark</span>
              </button>
            </div>
          )}
          {!collapsed && (
            <div className="sb-term">
              <div className="sb-dot" />
              <div className="sb-ti"><div className="sb-tl">ACTIVE TERM</div><div className="sb-tn">AY2026 Sem 1</div></div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', color: 'var(--slate)' }}>Oct–Feb</div>
            </div>
          )}
          <div
            className="sb-user"
            onClick={(e) => toggleProfileMenu(e.currentTarget)}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={profileOpen}
          >
            <div className="sb-ava">{ini}</div>
            <div className="sb-ui">
              <div className="sb-un">{user?.name}</div>
              <div className="sb-ur">{displayRole}</div>
            </div>
            <i className="fa-solid fa-ellipsis-vertical sb-user-kebab" />
          </div>
        </div>

        {profileOpen && (
          <div
            ref={profileRef}
            className="sb-fly"
            role="menu"
            style={{ bottom: profilePos.bottom, left: profilePos.left, top: 'auto' }}
          >
            <div className="sb-fly-title">{user?.name}</div>
            <NavLink to="/settings" className={({ isActive }) => `sb-fly-item ${isActive ? 'active' : ''}`} onClick={() => setProfileOpen(false)}>
              <i className="fa-solid fa-gear" />
              <span>Settings</span>
            </NavLink>
            <NavLink to="/profile" className={({ isActive }) => `sb-fly-item ${isActive ? 'active' : ''}`} onClick={() => setProfileOpen(false)}>
              <i className="fa-solid fa-circle-user" />
              <span>My Profile</span>
            </NavLink>
            <div className="sb-fly-item danger" onClick={() => { setProfileOpen(false); handleLogout(); }}>
              <i className="fa-solid fa-right-from-bracket" />
              <span>Logout</span>
            </div>
          </div>
        )}
      </aside>

      <div id="main">
        <header id="topbar">
          <button className="sb-hamburger" onClick={() => setMobileOpen((o) => !o)} aria-label="Open navigation">
            <i className="fa-solid fa-bars" />
          </button>
          <div className="tb-title">{title}</div>
          <div className="tb-right">
            <div className="tb-search"><i className="fa-solid fa-magnifying-glass" /><input type="text" placeholder="Search courses, lecturers…" /></div>
            <button className="tb-btn" onClick={(e) => { e.stopPropagation(); setNpOpen((o) => !o); }} aria-label="Notifications">
              <i className="fa-solid fa-bell" /><div className="tb-nd" />
            </button>
            <button className="tb-btn" onClick={() => navigate('/settings')} aria-label="Settings"><i className="fa-solid fa-gear" /></button>
            <div className="tb-ava" onClick={() => navigate('/profile')}>{ini}</div>
          </div>
        </header>

        <div id="np" ref={npRef} className={npOpen ? 'open' : ''}>
          <div className="np-head">
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--ink)' }}>Notifications</div>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--sea)', cursor: 'pointer', fontWeight: 600 }}>Mark all read</span>
          </div>
          {NOTIFICATIONS.map((n, i) => (
            <div key={i} className={`np-item ${n.unread ? 'unread' : ''}`}>
              <div className="np-ico" style={{ background: n.bg, color: n.col }}><i className={`fa-solid ${n.icon}`} /></div>
              <div>
                <div className="np-bt">{n.title}</div>
                <div className="np-bs">{n.sub}</div>
                <div className="np-bm">{n.time}</div>
              </div>
            </div>
          ))}
        </div>

        <div id="content">
          <div className="screen">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
